import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Modal, ModalBody, Row, Col, Button } from "reactstrap";
import { Icon } from "../../../components/Component";
import FormSelect from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import sitesApis from "../../../api/master/sites";
import contractorsApis from "../../../api/master/contractors";

interface SelectOptions {
  label: string;
  value: string;
}

interface AddBOQLineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  workLevelSetting: string | null;
  siteId: string | null;
  editData?: any;
  existingBoqLines?: any[];
}

const HIERARCHY_UNIT_TITLES: Record<string, string> = {
  TOWER_BLOCK: "Tower / Block",
  VILLA: "Villa",
  FLOOR: "Tower / Block",
  FLAT: "Tower / Block",
};

const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
  Tower: "TOWER_BLOCK",
  "Tower/Block": "TOWER_BLOCK",
  "Tower / Block": "TOWER_BLOCK",
  "Tower/ Block": "TOWER_BLOCK",
  Block: "TOWER_BLOCK",
  Villa: "VILLA",
  Floor: "FLOOR",
  Flat: "FLAT",
};

const AddBOQLineModal: React.FC<AddBOQLineModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  workLevelSetting,
  siteId,
  editData,
  existingBoqLines = [],
}) => {
  const methods = useForm({
    defaultValues: {
      boqLineName: "",
      contractorId: "",
      siteStructureUnitId: "",
      floorNo: "",
      siteStructureSubUnitId: "",
      locationId: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>([]);
  const [segmentLevel, setSegmentLevel] = useState<string | null>(null);

  const shouldShowHierarchy = !!workLevelSetting && workLevelSetting !== "NONE";
  const selectedUnitId = watch("siteStructureUnitId");
  const selectedFloorNo = watch("floorNo");

  // Filter out already-used values from dropdowns (exclude current editData)
  const editIndex = editData?.index;
  const otherBoqLines = useMemo(() => {
    return existingBoqLines.filter((_: any, idx: number) =>
      editData ? idx !== editIndex : true
    );
  }, [existingBoqLines, editData, editIndex]);

  const filteredContractorOptions = contractorOptions;

  const filteredTowerOptions = useMemo(() => {
    if (!shouldShowHierarchy) return towerOptions;
    if (workLevelSetting === "TOWER_BLOCK" || workLevelSetting === "VILLA") {
      // For Tower/Villa level: each tower can only be used once
      const usedUnitIds = otherBoqLines
        .map((line: any) => line.siteStructureUnitId?.toString())
        .filter(Boolean);
      return towerOptions.filter((opt) => !usedUnitIds.includes(opt.value));
    }
    return towerOptions;
  }, [towerOptions, otherBoqLines, shouldShowHierarchy, workLevelSetting]);

  const filteredFloorOptions = useMemo(() => {
    if (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
      return floorOptions;
    if (!selectedUnitId) return floorOptions;
    if (workLevelSetting === "FLOOR") {
      // For Floor level: Tower + Floor combination should not repeat
      const usedFloors = otherBoqLines
        .filter(
          (line: any) =>
            line.siteStructureUnitId?.toString() === selectedUnitId?.toString()
        )
        .map((line: any) => line.floorNo?.toString())
        .filter(Boolean);
      return floorOptions.filter((opt) => !usedFloors.includes(opt.value));
    }
    return floorOptions;
  }, [floorOptions, otherBoqLines, workLevelSetting, selectedUnitId]);

  const filteredFlatOptions = useMemo(() => {
    if (workLevelSetting !== "FLAT") return flatOptions;
    if (!selectedUnitId || !selectedFloorNo) return flatOptions;
    // For Flat level: Tower + Floor + Flat combination should not repeat
    const usedFlatIds = otherBoqLines
      .filter(
        (line: any) =>
          line.siteStructureUnitId?.toString() === selectedUnitId?.toString() &&
          line.floorNo?.toString() === selectedFloorNo?.toString()
      )
      .map((line: any) => line.siteStructureSubUnitId?.toString())
      .filter(Boolean);
    return flatOptions.filter((opt) => !usedFlatIds.includes(opt.value));
  }, [flatOptions, otherBoqLines, workLevelSetting, selectedUnitId, selectedFloorNo]);

  const hierarchyFieldLabel = shouldShowHierarchy
    ? HIERARCHY_UNIT_TITLES[workLevelSetting] ?? "Tower / Block"
    : "Tower / Block";

  const hierarchyFieldPlaceholder = (() => {
    switch (workLevelSetting) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  })();

  const loadTowerOptions = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? siteId;
      if (!resolvedSiteId) {
        setTowerOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
          siteId: resolvedSiteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          setTowerOptions(
            response.data.map((unit: any) => ({
              label: unit.unitName,
              value: unit.id?.toString(),
            }))
          );
        } else {
          setTowerOptions([]);
        }
      } catch (error) {
        console.log(error);
        setTowerOptions([]);
      }
    },
    [siteId]
  );

  const loadFloorOptions = useCallback(
    async (unitId?: string | number) => {
      if (!siteId || !unitId) {
        setFloorOptions([]);
        return;
      }
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          setFloorOptions(
            response.data.map((floor: any) => ({
              label: floor.floorNo,
              value: floor.floorNo?.toString(),
            }))
          );
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.log(error);
        setFloorOptions([]);
      }
    },
    [siteId]
  );

  const loadFlatOptions = useCallback(
    async (unitId?: string | number, floorNoValue?: string | null) => {
      if (!siteId || !unitId) {
        setFlatOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId,
          unitId,
          floorNo: floorNoValue ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          // Deduplicate flats by ID to avoid duplicate combinations
          const uniqueFlats = new Map<string, SelectOptions>();
          response.data.forEach((flat: any) => {
            const flatId = flat.id?.toString();
            if (flatId && !uniqueFlats.has(flatId)) {
              uniqueFlats.set(flatId, {
                label: flat.subUnitName || flat.unitName,
                value: flatId,
              });
            }
          });
          setFlatOptions(Array.from(uniqueFlats.values()));
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.log(error);
        setFlatOptions([]);
      }
    },
    [siteId]
  );

  const loadLocationOptions = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? siteId;
      if (!resolvedSiteId) {
        setLocationOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
          siteId: resolvedSiteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          setLocationOptions(
            response.data.map((loc: any) => ({
              label: loc.locationName,
              value: loc.id?.toString(),
            }))
          );
        } else {
          setLocationOptions([]);
        }
      } catch (error) {
        console.log(error);
        setLocationOptions([]);
      }
    },
    [siteId]
  );

  const fetchSiteHierarchy = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? siteId;
      if (!resolvedSiteId) {
        setSegmentLevel(null);
        return;
      }
      try {
        const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(
          resolvedSiteId
        );
        if (status && data) {
          setSegmentLevel(data);
        } else {
          setSegmentLevel(null);
        }
      } catch (error) {
        console.log("Error fetching site hierarchy:", error);
        setSegmentLevel(null);
      }
    },
    [siteId]
  );

  const fetchContractorList = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? siteId;
      if (!resolvedSiteId) {
        setContractorOptions([]);
        return;
      }
      try {
        const { data, status } = await contractorsApis.fetchContractorBySiteId({
          id: resolvedSiteId,
        });
        if (status) {
          setContractorOptions(
            data.map((item: any) => ({
              label: item?.companyName,
              value: item?.contractorID?.toString(),
            }))
          );
        } else {
          setContractorOptions([]);
        }
      } catch (error) {
        console.log(error);
        setContractorOptions([]);
      }
    },
    [siteId]
  );

  useEffect(() => {
    if (isOpen && siteId) {
      loadLocationOptions(siteId);
      fetchSiteHierarchy(siteId);
      fetchContractorList(siteId);
    }
  }, [isOpen, siteId, loadLocationOptions, fetchSiteHierarchy, fetchContractorList]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!shouldShowHierarchy) {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      return;
    }
    if (siteId) {
      loadTowerOptions(siteId);
    }
  }, [isOpen, shouldShowHierarchy, loadTowerOptions, siteId]);

  useEffect(() => {
    if (
      !shouldShowHierarchy ||
      !selectedUnitId ||
      (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
    ) {
      setFloorOptions([]);
      if (watch("floorNo")) {
        setValue("floorNo", "");
      }
      return;
    }
    loadFloorOptions(selectedUnitId);
  }, [
    shouldShowHierarchy,
    selectedUnitId,
    workLevelSetting,
    loadFloorOptions,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (
      !shouldShowHierarchy ||
      workLevelSetting !== "FLAT" ||
      !selectedUnitId
    ) {
      setFlatOptions([]);
      if (watch("siteStructureSubUnitId")) {
        setValue("siteStructureSubUnitId", "");
      }
      return;
    }
    loadFlatOptions(selectedUnitId, selectedFloorNo ?? null);
  }, [
    shouldShowHierarchy,
    workLevelSetting,
    selectedUnitId,
    selectedFloorNo,
    loadFlatOptions,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          boqLineName: editData.boqLineName || "",
          contractorId: editData.contractorId?.toString() || "",
          siteStructureUnitId:
            editData.siteStructureUnitId?.toString() ||
            (shouldShowHierarchy ? "" : undefined),
          floorNo: editData.floorNo || (shouldShowHierarchy ? "" : undefined),
          siteStructureSubUnitId:
            editData.siteStructureSubUnitId?.toString() ||
            (shouldShowHierarchy ? "" : undefined),
          locationId:
            editData.locationId?.toString() ||
            (shouldShowHierarchy ? "" : undefined),
        });
      } else {
        reset({
          boqLineName: "",
          contractorId: "",
          siteStructureUnitId: shouldShowHierarchy ? "" : undefined,
          floorNo: shouldShowHierarchy ? "" : undefined,
          siteStructureSubUnitId: shouldShowHierarchy ? "" : undefined,
          locationId: shouldShowHierarchy ? "" : undefined,
        });
      }
    }
  }, [isOpen, reset, shouldShowHierarchy, editData]);

  const onSubmitForm = (data: any) => {
    // Find names from options
    const towerName = data.siteStructureUnitId
      ? towerOptions.find(
          (opt) => opt.value === data.siteStructureUnitId?.toString()
        )?.label
      : null;

    const flatName = data.siteStructureSubUnitId
      ? flatOptions.find(
          (opt) => opt.value === data.siteStructureSubUnitId?.toString()
        )?.label
      : null;

    const locationName = data.locationId
      ? locationOptions.find((opt) => opt.value === data.locationId?.toString())
          ?.label
      : null;

    const contractorName = data.contractorId
      ? contractorOptions.find(
          (opt) => opt.value === data.contractorId?.toString()
        )?.label
      : null;

    const boqLineData = {
      boqLineName: data.boqLineName || "",
      contractorId: data.contractorId || null,
      contractorName: contractorName || null,
      workLevelSetting: workLevelSetting || null,
      siteStructureUnitId: shouldShowHierarchy
        ? data.siteStructureUnitId || null
        : null,
      siteStructureUnitName: towerName || null,
      floorNo: shouldShowHierarchy ? data.floorNo || null : null,
      siteStructureSubUnitId: shouldShowHierarchy
        ? data.siteStructureSubUnitId || null
        : null,
      siteStructureSubUnitName: flatName || null,
      locationId: shouldShowHierarchy ? data.locationId || null : null,
      locationName: locationName || null,
      materials: [],
    };
    onSubmit(boqLineData);
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            onClose();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <h5 className="title">{editData ? "Edit BOQ Line" : "Add BOQ Line"}</h5>
        <div style={{ marginTop: "15px" }}></div>
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmitForm)(e);
            }}
          >
            <Row className="mt-12">
              <div className="col-md-6">
                <FormInput
                  name="boqLineName"
                  label="BOQ Line Name"
                  required={true}
                  type="text"
                  placeholder="Enter BOQ Line Name"
                />
              </div>
              <div className="col-md-6">
                <FormSelect
                  name="contractorId"
                  label="Contractor"
                  required={true}
                  options={filteredContractorOptions}
                  placeholder="Select Contractor"
                />
              </div>
            </Row>
            {shouldShowHierarchy && (
              <Row className="mt-4">
                <div className="col-md-6">
                  <FormSelect
                    name="siteStructureUnitId"
                    label={hierarchyFieldLabel}
                    required={shouldShowHierarchy}
                    options={filteredTowerOptions}
                    placeholder={hierarchyFieldPlaceholder}
                  />
                </div>
              </Row>
            )}
            {shouldShowHierarchy && (
              <>
                {(workLevelSetting === "FLOOR" ||
                  workLevelSetting === "FLAT") && (
                  <Row className="mt-4">
                    <div className="col-md-6">
                      <FormSelect
                        name="floorNo"
                        label="Floor"
                        required={true}
                        options={filteredFloorOptions}
                        placeholder="Select Floor"
                      />
                    </div>
                    {workLevelSetting === "FLAT" ? (
                      <div className="col-md-6">
                        <FormSelect
                          name="siteStructureSubUnitId"
                          label="Flat"
                          required={workLevelSetting === "FLAT" && segmentLevel !== "Flat"}
                          options={filteredFlatOptions}
                          placeholder="Select Flat"
                        />
                      </div>
                    ) : (
                      <div className="col-md-6">
                        <FormSelect
                          name="locationId"
                          label="Location"
                          options={locationOptions}
                          placeholder="Select Location"
                        />
                      </div>
                    )}
                  </Row>
                )}
                {workLevelSetting === "FLAT" && (
                  <Row className="mt-4">
                    <div className="col-md-6">
                      <FormSelect
                        name="locationId"
                        label="Location"
                        options={locationOptions}
                        placeholder="Select Location"
                      />
                    </div>
                  </Row>
                )}
                {(workLevelSetting === "TOWER_BLOCK" ||
                  workLevelSetting === "VILLA") && (
                  <Row className="mt-4">
                    <div className="col-md-6">
                      <FormSelect
                        name="locationId"
                        label="Location"
                        options={locationOptions}
                        placeholder="Select Location"
                      />
                    </div>
                  </Row>
                )}
              </>
            )}
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      {editData ? "Update BOQ Line" : "Add BOQ Line"}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onClose();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default AddBOQLineModal;
